import {
  BellRing,
  Bot,
  CalendarDays,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  FileText,
  History,
  LayoutTemplate,
  Logs,
  type LucideIcon,
  MapPin,
  MessageCircleQuestion,
  MessagesSquare,
  ScrollText,
  Settings,
  ShieldCheck,
  Stamp,
  TestTubeDiagonal,
  Users,
} from 'lucide-react';
import { APP_ROLES, type AppRole } from './role';

/**
 * Адреса разделов меню. Страница раздела берёт свой адрес отсюда в `route.ts`
 * (`path: NAV_PATH.journal`), так меню и маршрут не расходятся.
 */
export const NAV_PATH = {
  brigadier: '/brigadier',
  personnel: '/personnel',
  brigades: '/brigades',
  hr: '/hr',
  documents: '/documents',
  users: '/admin/users',
  template: '/admin/template',
  rules: '/rules',
  approvals: '/rules/approvals',
  goldens: '/rules/goldens',
  history: '/rules/history',
  registry: '/rules/registry',
  schedules: '/schedules',
  calendar: '/calendar',
  agentData: '/admin/agent-data',
  journal: '/journal',
  myAgents: '/my-agents',
  assistant: '/assistant',
  // «Настройки» (007 T037): пункт внизу меню, над карточкой пользователя; разделы — `SETTINGS_SECTIONS`.
  settings: '/settings',
} as const;

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles: readonly AppRole[];
  /**
   * Серый счётчик у пункта (003, FR-022): `openQuestions` — вопросы без ответа по табелям в работе. Напоминание,
   * а не тревога: цвет приглушённый.
   */
  counter?: 'openQuestions';
}

const ALL: readonly AppRole[] = APP_ROLES;

/**
 * Меню по ролям — по таблице «Права ролей» spec.md: пункт есть у роли, которой разрешено
 * хотя бы чтение раздела. Порядок — порядок в меню. Домашняя страница роли — `homeOf` из `lib/role.tsx` (004 R18).
 */
export const NAV_ITEMS: readonly NavItem[] = [
  // Табель 001: состав, правка часов, «Завершить» — своя бригада. Счётчик — вопросы без ответа (003).
  {
    to: NAV_PATH.brigadier,
    label: 'Табели',
    icon: ClipboardList,
    roles: ['brigadier'],
    counter: 'openQuestions',
  },
  // Табель 001: проверка, возврат, подпись.
  { to: NAV_PATH.hr, label: 'На проверку', icon: ClipboardCheck, roles: ['hr'] },
  // Документы: вводить, править, отменять — только кадры.
  { to: NAV_PATH.documents, label: 'Документы', icon: FileText, roles: ['hr'] },
  // Учётные записи и роли.
  { to: NAV_PATH.users, label: 'Пользователи и роли', icon: Users, roles: ['sysadmin'] },
  // Календарь и шаблон: все шаги черновика и утверждение.
  { to: NAV_PATH.template, label: 'Шаблон правил', icon: LayoutTemplate, roles: ['sysadmin'] },
  // «Правила расчёта» (004): правила объекта простыми словами, история изменений — видят все роли.
  {
    to: NAV_PATH.rules,
    label: 'Правила расчёта',
    icon: ScrollText,
    roles: ALL,
  },
  // «Помощник» (006): свои разговоры с ИИ-агентом — роли с правом `agent.thread` (006, «Права ролей»; бригадир и кадры —
  // нет). Без права «ИИ-агент» и при выключенной функции 006 пункта нет: его убирает каркас (`AppShell`).
  {
    to: NAV_PATH.assistant,
    label: 'Помощник',
    icon: MessagesSquare,
    roles: ['rules_admin', 'approver', 'auditor', 'sysadmin'],
  },
  // Заявки: объект — администратор правил и утверждающий; календарь, шаблон — администратор системы.
  { to: NAV_PATH.approvals, label: 'Заявки', icon: Stamp, roles: ['rules_admin', 'approver', 'sysadmin'] },
  // Проверочные примеры объекта (эталоны 002): персональные данные дня — администратор правил и утверждающий.
  {
    to: NAV_PATH.goldens,
    label: 'Проверочные примеры',
    icon: TestTubeDiagonal,
    roles: ['rules_admin', 'approver'],
  },
  // История версий с основаниями — все роли.
  { to: NAV_PATH.history, label: 'История правил', icon: History, roles: ALL },
  // Реестр расчёта (вопросы о правилах расчёта, Q-xx): читать — все, заводить — администратор правил и утверждающий.
  { to: NAV_PATH.registry, label: 'Реестр расчёта', icon: MessageCircleQuestion, roles: ALL },
  // Графики объекта и их назначения — видят все роли.
  { to: NAV_PATH.schedules, label: 'Графики', icon: CalendarRange, roles: ALL },
  // Производственный календарь — видят все роли, правит администратор системы.
  { to: NAV_PATH.calendar, label: 'Календарь', icon: CalendarDays, roles: ALL },
  // Передача данных агентам на объекте.
  { to: NAV_PATH.agentData, label: 'Данные для ИИ-агентов', icon: ShieldCheck, roles: ['sysadmin'] },
  // Журнал действий.
  {
    to: NAV_PATH.journal,
    label: 'Журнал действий',
    icon: Logs,
    roles: ['rules_admin', 'approver', 'auditor', 'sysadmin'],
  },
  // Свои подключения ИИ-агентов и выборки — роли, которые подключают агента (005, «Права ролей»; бригадир и кадры — нет).
  {
    to: NAV_PATH.myAgents,
    label: 'Мои ИИ-клиенты',
    icon: Bot,
    roles: ['rules_admin', 'approver', 'auditor', 'sysadmin'],
  },
];

/** Пункты меню роли, у которых уже есть страница (`registered` — пути из `routes.generated`). */
export function navFor(role: AppRole, registered: Iterable<string>): NavItem[] {
  const has = new Set(registered);
  return NAV_ITEMS.filter((n) => n.roles.includes(role) && has.has(n.to));
}

/* ---------- «Настройки» (007 T037; spec.md FR-001; дизайн-система SettingsLayout, ux.md §11) ---------- */

/** Маршрут страницы «Настройки» (`pages/Settings/route.ts`): разделы — вложенные адреса. */
export const SETTINGS_ROUTE = `${NAV_PATH.settings}/*`;

/** Адреса разделов «Настроек». Код площадки в адресе — `:site` (contracts/http-api.md). */
export const SETTINGS_PATH = {
  sites: `${NAV_PATH.settings}/sites`,
  site: (code: string) => `${NAV_PATH.settings}/sites/${encodeURIComponent(code)}`,
  alerts: `${NAV_PATH.settings}/alerts`,
} as const;

/** Открыт экран «Настройки» (любой раздел). */
export function isSettingsPath(pathname: string): boolean {
  return pathname === NAV_PATH.settings || pathname.startsWith(`${NAV_PATH.settings}/`);
}

/** Группы навигации разделов — по порядку дизайн-системы (SettingsLayout). */
const SETTINGS_GROUPS = [
  { group: 'general', label: 'Общие' },
  { group: 'access', label: 'Доступ' },
  { group: 'notifications', label: 'Уведомления' },
  { group: 'personal', label: 'Личное' },
  { group: 'security', label: 'Безопасность' },
  { group: 'billing', label: 'Оплата' },
] as const;
export type SettingsGroup = (typeof SETTINGS_GROUPS)[number]['group'];

export type SettingsSectionKey = 'sites' | 'alerts';

export interface SettingsSection {
  key: SettingsSectionKey;
  group: SettingsGroup;
  label: string;
  icon: LucideIcon;
  to: string;
  roles: readonly AppRole[];
}

/**
 * Разделы «Настроек» по таблице «Права ролей» spec.md 007: площадки и их тревоги — администратор системы. Страницы
 * «Пользователи и роли» в сервисе нет (роли выдаются командами сервера); когда она появится — группа «Доступ».
 */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    key: 'sites',
    group: 'general',
    label: 'Площадки',
    icon: MapPin,
    to: SETTINGS_PATH.sites,
    roles: ['sysadmin'],
  },
  {
    key: 'alerts',
    group: 'notifications',
    label: 'Тревоги площадок',
    icon: BellRing,
    to: SETTINGS_PATH.alerts,
    roles: ['sysadmin'],
  },
];

export interface SettingsGroupItems {
  group: SettingsGroup;
  label: string;
  sections: SettingsSection[];
}

/** Разделы роли группами по порядку; пустых групп нет (MUST, ux.md §11). */
export function settingsFor(role: AppRole): SettingsGroupItems[] {
  const allowed = SETTINGS_SECTIONS.filter((s) => s.roles.includes(role));
  return SETTINGS_GROUPS.map((g) => ({
    group: g.group,
    label: g.label,
    sections: allowed.filter((s) => s.group === g.group),
  })).filter((g) => g.sections.length > 0);
}

/** Пункт «Настройки» внизу бокового меню. */
export const SETTINGS_ITEM = { to: NAV_PATH.settings, label: 'Настройки', icon: Settings } as const;

/** Пункт «Настройки» роли: есть хотя бы один раздел с правом и страница среди маршрутов (`routes.generated`). */
export function settingsItemFor(role: AppRole, registered: Iterable<string>): typeof SETTINGS_ITEM | null {
  if (!new Set(registered).has(SETTINGS_ROUTE)) return null;
  return settingsFor(role).length > 0 ? SETTINGS_ITEM : null;
}

/** Адрес документа «Правила на дату» для печати (004, FR-013, research R19). */
export const RULES_PRINT_PATH = `${NAV_PATH.rules}/print`;

/**
 * Адрес «Правил расчёта» (004, contracts/ui.md; research R20): `date` — правила на дату; `start`, `end` — период
 * табеля (выбор версии, если в периоде их две); `from` — откуда пришли («Вернуться к табелю»); `group` — правило
 * (`#<группа>`). Пустые части не идут.
 */
export function rulesHref(
  o: {
    date?: string;
    start?: string;
    end?: string;
    from?: string;
    group?: string;
    /** `history` — сразу «История изменений» (004, US3: ссылка «Ещё N пересчётов» из плашки табеля). */
    view?: 'history';
  } = {},
): string {
  const p = new URLSearchParams();
  for (const k of ['date', 'start', 'end', 'from', 'view'] as const) {
    const v = o[k];
    if (v) p.set(k, v);
  }
  const q = p.toString();
  return `${NAV_PATH.rules}${q ? `?${q}` : ''}${o.group ? `#${o.group}` : ''}`;
}

/** Spec 010 navigation uses explicit directory capabilities, independently of legacy roles. */
export function directoryNavFor(capabilities: readonly string[]): NavItem[] {
  return [
    ...(capabilities.some((c) =>
      [
        'personnel.read',
        'identity.review',
        'identity.correct',
        'cases.manage',
        'source_mapping.manage',
        'accounts.person_bind',
        'membership.manage',
      ].includes(c),
    )
      ? [{ to: NAV_PATH.personnel, label: 'Персонал', icon: Users, roles: ALL }]
      : []),
    ...(capabilities.some((c) => ['brigade.read', 'brigade.create_own', 'membership.read'].includes(c))
      ? [{ to: NAV_PATH.brigades, label: 'Бригады', icon: Users, roles: ALL }]
      : []),
  ];
}
